import React, { FC, useState } from 'react';
import { Menu, MenuItem, Typography } from '@mui/material';
import { IComment } from './constants';
import { IconCopy, IconPencil, IconTrash } from '../../../../utils/icons';
import { MenuIcon } from '../../../../components/MenuOptions/MenuIcon';
import { useVideoFeedback } from './useVideoFeedback';

interface Props {
  comment: IComment;
  /** Edit: the author's own comment while the review is open. */
  canEdit: boolean;
  /** Delete: the author's own comment, or (staff) the client's. */
  canDelete: boolean;
  onEdit: () => void;
  /** Set when the viewer may copy this comment onto other videos. */
  onApplyToVideos?: () => void;
}

export const CommentMenu: FC<Props> = ({ comment, canEdit, canDelete, onEdit, onApplyToVideos }) => {
  const { onDeleteComment: onDelete } = useVideoFeedback();
  const menuId = `menu-box-${comment.id || Math.random()}`;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const closeMenu = () => setAnchorEl(null);

  // CommentList decides who gets which item; nothing to offer, no kebab.
  if (!canEdit && !canDelete && !onApplyToVideos) return null;

  const getOption = (Icon: any, text: string, callback: any) => (
    <MenuItem
      className="!p-3 !px-4 flex gap-2 items-center min-w-[220px]"
      onClick={() => {
        callback();
        closeMenu();
      }}
    >
      <Icon width={24} height={24} size={24} stroke="#B0B0B0" />
      <Typography color="var(--text-2)">
        {text}
      </Typography>
    </MenuItem>
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <MenuIcon id={menuId} element={anchorEl} onClick={setAnchorEl} />
      <Menu
        anchorEl={anchorEl}
        onClose={() => closeMenu()}
        id="single-video-menu"
        aria-labelledby={menuId || 'single-video-menu-btn'}
        open={!!anchorEl}
        className="py-2"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {onApplyToVideos && getOption(IconCopy, 'Apply to other videos', () => onApplyToVideos())}
        {canEdit && getOption(IconPencil, 'Edit Comment', () => onEdit())}
        {canDelete && getOption(IconTrash, 'Delete Comment', () => onDelete(comment.id))}
      </Menu>
    </div>
  );
};
